/** biome-ignore-all lint/style/noMagicNumbers: valid constant */
import { relations, sql } from "drizzle-orm";
import { index, pgTable, unique } from "drizzle-orm/pg-core";
import { createInsertSchema, createUpdateSchema } from "drizzle-valibot";
import {
  array,
  boolean,
  description,
  intersect,
  literal,
  maxLength,
  minLength,
  object,
  omit,
  picklist,
  pipe,
  string,
  union,
  uuid,
} from "valibot";
import { user } from "./auth.schema";
import { slugField } from "./validators";

/**
 * The one subject an article is filed under.
 *
 * Separate from `tags`, which is an open list carrying stack names for the foot
 * of a piece. The category is a closed set: it is the chip on a card and the
 * filter on the listing, and both need a fixed, translatable vocabulary.
 */
export const ARTICLE_CATEGORIES = [
  "getting-started",
  "system-design",
  "operations",
] as const;
export type ArticleCategory = (typeof ARTICLE_CATEGORIES)[number];

export const articles = pgTable(
  "articles",
  (t) => ({
    authorId: t
      .text()
      .references(() => user.id, { onDelete: "cascade" })
      .notNull(),
    category: t
      .varchar({ length: 32 })
      .$type<ArticleCategory>()
      .notNull()
      .default("getting-started"),
    createdAt: t.timestamp().defaultNow().notNull(),
    id: t.uuid().notNull().primaryKey().defaultRandom(),
    imageUrl: t.varchar({ length: 255 }),
    isDraft: t.boolean().notNull().default(false),
    likes: t.integer().notNull().default(0),
    /**
     * When the article first went public. Set only by `blogService.publish`,
     * never by an editor, so a re-publish keeps the original date. Rows that
     * predate the column were backfilled from `created_at` read as UTC.
     */
    publishedAt: t.timestamp({ mode: "date", withTimezone: true }),
    tags: t.text().array(),
    updatedAt: t
      .timestamp({ mode: "date", withTimezone: true })
      .$onUpdate(() => new Date()),
  }),
  (t) => [
    /**
     * The order every listing uses (`newestFirst` in packages/api blog.ts).
     * `created_at` has no time zone, and the cast a bare coalesce would give it
     * depends on the session's, which Postgres will not index; it is read as
     * UTC, as the published_at backfill does.
     *
     * Ascending: Postgres scans it backwards for the listings' DESC, and
     * drizzle-kit records an expression's direction as ascending whatever the
     * SQL says, so a DESC index would be dropped and rebuilt by every push.
     */
    index("articles_published_at_idx").on(
      sql`coalesce(${t.publishedAt}, ${t.createdAt} at time zone 'UTC')`
    ),
  ]
);

export const articleTranslations = pgTable(
  "article_translations",
  (t) => ({
    articleId: t
      .uuid()
      .references(() => articles.id, { onDelete: "cascade" })
      .notNull(),
    content: t.text(),
    createdAt: t.timestamp().defaultNow().notNull(),
    description: t.varchar({ length: 255 }),
    id: t.uuid().notNull().primaryKey().defaultRandom(),
    /** Cover alt text in this locale; empty for a purely decorative cover. */
    imageAlt: t.varchar({ length: 255 }),
    locale: t.varchar({ length: 5 }).notNull(),
    slug: t.varchar({ length: 255 }).notNull(),
    title: t.varchar({ length: 255 }).notNull(),
    updatedAt: t
      .timestamp({ mode: "date", withTimezone: true })
      .$onUpdate(() => new Date()),
  }),
  (t) => [
    unique("article_translations_article_id_locale_unique").on(
      t.articleId,
      t.locale
    ),
    unique("article_translations_slug_locale_unique").on(t.slug, t.locale),
  ]
);

export const articleRelations = relations(articles, (t) => ({
  author: t.one(user, {
    fields: [articles.authorId],
    references: [user.id],
  }),
  translations: t.many(articleTranslations),
  views: t.many(articleViews),
}));

export const articleTranslationRelations = relations(
  articleTranslations,
  (t) => ({
    article: t.one(articles, {
      fields: [articleTranslations.articleId],
      references: [articles.id],
    }),
  })
);

export const articleLikes = pgTable("article_likes", (t) => ({
  createdAt: t.timestamp().defaultNow().notNull(),
  id: t.text().notNull().primaryKey(),
}));

export const articleViews = pgTable("article_views", (t) => ({
  articleId: t
    .uuid()
    .references(() => articles.id, { onDelete: "cascade" })
    .notNull(),
  createdAt: t.timestamp().defaultNow().notNull(),
  id: t.uuid().notNull().primaryKey().defaultRandom(),
}));

export const articleViewRelations = relations(articleViews, (t) => ({
  article: t.one(articles, {
    fields: [articleViews.articleId],
    references: [articles.id],
  }),
}));

const RequiredTitleField = pipe(
  string(),
  minLength(1, "Title is required"),
  maxLength(255, "Title cannot exceed 255 characters")
);

const OptionalTitleField = union([
  pipe(string(), maxLength(255, "Title cannot exceed 255 characters")),
  literal(""),
]);

const OptionalSlugField = union([slugField, literal("")]);

const DescriptionField = union([
  pipe(string(), maxLength(255, "Description cannot exceed 255 characters")),
  literal(""),
]);

const ContentField = union([string(), literal("")]);

/** Search engines and screen readers cut alt text off around 125 characters. */
export const MAX_IMAGE_ALT_LENGTH = 125;

/** Empty means a purely decorative cover. Required so forms always send it. */
const ImageAltField = union([
  pipe(
    string(),
    maxLength(
      MAX_IMAGE_ALT_LENGTH,
      `Alt text cannot exceed ${MAX_IMAGE_ALT_LENGTH} characters`
    )
  ),
  literal(""),
]);

export const EnArticleTranslationSchema = object({
  content: ContentField,
  description: DescriptionField,
  imageAlt: ImageAltField,
  slug: slugField,
  title: RequiredTitleField,
});

export const LvArticleTranslationSchema = object({
  content: ContentField,
  description: DescriptionField,
  imageAlt: ImageAltField,
  slug: OptionalSlugField,
  title: OptionalTitleField,
});

export const ArticleTranslationsMapSchema = object({
  en: EnArticleTranslationSchema,
  lv: LvArticleTranslationSchema,
});

export const ArticleBaseSchema = object({
  category: picklist(ARTICLE_CATEGORIES),
  isDraft: union([boolean(), literal(false)]),
  tags: array(string()),
  thumbnail: pipe(string(), description("File upload for article thumbnail")),
  translations: ArticleTranslationsMapSchema,
});

export const CreateArticleSchema = intersect([
  omit(
    createInsertSchema(articles, {
      authorId: string(),
      category: ArticleBaseSchema.entries.category,
      isDraft: ArticleBaseSchema.entries.isDraft,
      tags: ArticleBaseSchema.entries.tags,
    }),
    // publishedAt belongs to blogService.publish, never to an editor's payload.
    ["id", "createdAt", "updatedAt", "publishedAt"]
  ),
  object({
    thumbnail: ArticleBaseSchema.entries.thumbnail,
    translations: ArticleTranslationsMapSchema,
  }),
]);

export const UpdateArticleSchema = intersect([
  omit(
    createUpdateSchema(articles, {
      category: ArticleBaseSchema.entries.category,
      id: pipe(string(), uuid()),
      isDraft: ArticleBaseSchema.entries.isDraft,
      tags: ArticleBaseSchema.entries.tags,
    }),
    ["createdAt", "updatedAt", "publishedAt"]
  ),
  object({
    thumbnail: ArticleBaseSchema.entries.thumbnail,
    translations: ArticleTranslationsMapSchema,
  }),
]);
